/**
 * NmbrsLinkPanel Component
 *
 * Manages employee linking/unlinking to Nmbrs accounts.
 * Story 1.2: Medewerker Koppeling & Ontkoppeling
 */

import { useState } from 'react';
import { useLinkedEmployees, useLinkEmployee, useUnlinkEmployee, useBulkLinkEmployees, type BulkLinkReport } from '../hooks/useNmbrs';
import { useEmployees } from '../../employees/useEmployees';
import { useAuth } from '../../auth/AuthContext';

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  active: { bg: 'bg-green-100', text: 'text-green-800' },
  paused: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  error: { bg: 'bg-red-100', text: 'text-red-800' },
};

export function NmbrsLinkPanel() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const { data: linksData, isLoading: linksLoading } = useLinkedEmployees();
  const { data: employeesData } = useEmployees();
  const linkMutation = useLinkEmployee();
  const unlinkMutation = useUnlinkEmployee();
  const bulkLinkMutation = useBulkLinkEmployees();

  const [showForm, setShowForm] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>('');
  const [nmbrsEmployeeId, setNmbrsEmployeeId] = useState<string>('');
  const [unlinkConfirm, setUnlinkConfirm] = useState<number | null>(null);
  const [bulkReport, setBulkReport] = useState<BulkLinkReport | null>(null);

  const links = linksData?.data ?? [];
  const employees = employeesData?.data ?? [];
  const linkedEmployeeIds = new Set(links.map((l) => l.employeeId));
  const unlinkedEmployees = employees.filter((e) => !linkedEmployeeIds.has(e.id));

  const handleLink = async () => {
    const empId = parseInt(selectedEmployeeId, 10);
    const nmbrsId = parseInt(nmbrsEmployeeId, 10);
    if (isNaN(empId) || isNaN(nmbrsId) || empId <= 0 || nmbrsId <= 0) return;

    try {
      await linkMutation.mutateAsync({ employeeId: empId, nmbrsEmployeeId: nmbrsId });
      setSelectedEmployeeId('');
      setNmbrsEmployeeId('');
      setShowForm(false);
    } catch {
      // Error is handled by mutation state
    }
  };

  const handleUnlink = async (employeeId: number) => {
    try {
      await unlinkMutation.mutateAsync(employeeId);
      setUnlinkConfirm(null);
    } catch {
      // Error is handled by mutation state
    }
  };

  const getEmployeeName = (employeeId: number) => {
    const emp = employees.find((e) => e.id === employeeId);
    return emp ? emp.name : `Medewerker #${employeeId}`;
  };

  const handleBulkDryRun = async () => {
    try {
      const result = await bulkLinkMutation.mutateAsync(false);
      setBulkReport(result.data);
    } catch {
      // Error is handled by mutation state
    }
  };

  const handleBulkApply = async () => {
    try {
      const result = await bulkLinkMutation.mutateAsync(true);
      setBulkReport(result.data);
    } catch {
      // Error is handled by mutation state
    }
  };

  const closeBulkReport = () => {
    setBulkReport(null);
    bulkLinkMutation.reset();
  };

  if (linksLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5A7A]"></div>
        <span className="ml-2 text-sm text-gray-500">Koppelingen laden...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-[#1E293B]">Medewerker Koppelingen</h3>
          <p className="text-sm text-gray-500">
            {links.length} medewerker{links.length !== 1 ? 's' : ''} gekoppeld aan Nmbrs
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && !showForm && !bulkReport && (
            <button
              onClick={handleBulkDryRun}
              disabled={bulkLinkMutation.isPending}
              className="px-4 py-2 bg-white text-[#2F5A7A] text-sm font-medium border border-[#2F5A7A] rounded-lg hover:bg-[#2F5A7A]/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {bulkLinkMutation.isPending ? 'Bezig...' : 'Alle medewerkers automatisch koppelen'}
            </button>
          )}
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-[#2F5A7A] text-white text-sm rounded-lg hover:bg-[#254963] transition-colors"
            >
              + Koppelen
            </button>
          )}
        </div>
      </div>

      {/* Bulk-koppel rapport (Story 1.1, admin-only — AC 7) */}
      {isAdmin && bulkReport && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#1E293B]">
              {bulkReport.mode === 'dry_run' ? 'Voorbeeld bulk-koppeling (nog niet uitgevoerd)' : 'Bulk-koppeling uitgevoerd'}
            </h4>
            <button onClick={closeBulkReport} className="text-sm text-[#54949E] hover:text-[#2F5A7A]">
              Sluiten
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-[#B7D945]/10 border border-[#E2E8F0] rounded-lg p-3">
              <div className="text-xs text-gray-500">
                {bulkReport.mode === 'apply' ? 'Gekoppeld' : 'Te koppelen'}
              </div>
              <div className="text-lg font-semibold text-[#1E293B]">
                {bulkReport.mode === 'apply' ? bulkReport.linked ?? 0 : bulkReport.matched.length}
              </div>
            </div>
            <div className="bg-gray-50 border border-[#E2E8F0] rounded-lg p-3">
              <div className="text-xs text-gray-500">Al gekoppeld</div>
              <div className="text-lg font-semibold text-[#1E293B]">{bulkReport.alreadyLinked.length}</div>
            </div>
            <div className="bg-gray-50 border border-[#E2E8F0] rounded-lg p-3">
              <div className="text-xs text-gray-500">Niet gevonden</div>
              <div className="text-lg font-semibold text-[#1E293B]">{bulkReport.notFound.length}</div>
            </div>
            <div className="bg-gray-50 border border-[#E2E8F0] rounded-lg p-3">
              <div className="text-xs text-gray-500">Ambigu</div>
              <div className="text-lg font-semibold text-[#1E293B]">{bulkReport.ambiguous.length}</div>
            </div>
          </div>

          {bulkReport.matched.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">
                {bulkReport.mode === 'apply' ? 'Gekoppelde medewerkers' : 'Voorgestelde koppelingen'}
              </p>
              <ul className="text-sm text-[#1E293B] space-y-1">
                {bulkReport.matched.map((m) => (
                  <li key={m.employeeId}>
                    {m.employeeName} → Nmbrs #{m.nmbrsEmployeeId} ({m.nmbrsDisplayName})
                  </li>
                ))}
              </ul>
            </div>
          )}

          {bulkReport.ambiguous.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Ambigu — niet automatisch gekoppeld</p>
              <ul className="text-sm text-[#1E293B] space-y-1">
                {bulkReport.ambiguous.map((a) => (
                  <li key={a.employeeId}>
                    {a.employeeName}: {a.candidates.map((c) => c.displayName).join(', ')}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {bulkReport.notFound.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-500 mb-1">Niet gevonden in Nmbrs</p>
              <ul className="text-sm text-[#1E293B] space-y-1">
                {bulkReport.notFound.map((n) => (
                  <li key={n.employeeId}>{n.employeeName}</li>
                ))}
              </ul>
            </div>
          )}

          {bulkReport.mode === 'apply' && bulkReport.failed && bulkReport.failed.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[#D64545] mb-1">Mislukte koppelingen</p>
              <ul className="text-sm text-[#D64545] space-y-1">
                {bulkReport.failed.map((f) => (
                  <li key={f.employeeId}>Medewerker #{f.employeeId}: {f.error}</li>
                ))}
              </ul>
            </div>
          )}

          {bulkLinkMutation.isError && (
            <p className="text-sm text-[#D64545]">
              {bulkLinkMutation.error instanceof Error ? bulkLinkMutation.error.message : 'Bulk-koppeling mislukt'}
            </p>
          )}

          {bulkReport.mode === 'dry_run' && (
            <div className="flex gap-2">
              <button
                onClick={handleBulkApply}
                disabled={bulkLinkMutation.isPending || bulkReport.matched.length === 0}
                className="px-4 py-2 bg-[#2F5A7A] text-white text-sm rounded-lg hover:bg-[#254963] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {bulkLinkMutation.isPending ? 'Bezig...' : `Bevestigen en ${bulkReport.matched.length} medewerker${bulkReport.matched.length !== 1 ? 's' : ''} koppelen`}
              </button>
              <button
                onClick={closeBulkReport}
                className="px-4 py-2 text-gray-600 text-sm hover:text-gray-800"
              >
                Annuleren
              </button>
            </div>
          )}
        </div>
      )}

      {/* Link Form */}
      {showForm && (
        <div className="bg-gray-50 border border-[#E2E8F0] rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-[#1E293B]">Nieuwe koppeling</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Medewerker</label>
              <select
                value={selectedEmployeeId}
                onChange={(e) => setSelectedEmployeeId(e.target.value)}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
              >
                <option value="">Selecteer medewerker...</option>
                {unlinkedEmployees.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nmbrs Medewerker-ID</label>
              <input
                type="number"
                value={nmbrsEmployeeId}
                onChange={(e) => setNmbrsEmployeeId(e.target.value)}
                placeholder="bijv. 12345"
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:ring-2 focus:ring-[#54949E] focus:border-[#54949E]"
                min="1"
              />
            </div>
          </div>

          {linkMutation.isError && (
            <p className="text-sm text-red-600">
              {linkMutation.error instanceof Error ? linkMutation.error.message : 'Koppeling mislukt'}
            </p>
          )}

          <div className="flex gap-2">
            <button
              onClick={handleLink}
              disabled={!selectedEmployeeId || !nmbrsEmployeeId || linkMutation.isPending}
              className="px-4 py-2 bg-[#2F5A7A] text-white text-sm rounded-lg hover:bg-[#254963] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {linkMutation.isPending ? 'Koppelen...' : 'Koppelen'}
            </button>
            <button
              onClick={() => { setShowForm(false); setSelectedEmployeeId(''); setNmbrsEmployeeId(''); }}
              className="px-4 py-2 text-gray-600 text-sm hover:text-gray-800"
            >
              Annuleren
            </button>
          </div>
        </div>
      )}

      {/* Linked Employees Table */}
      {links.length > 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-[#E2E8F0]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Medewerker</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nmbrs ID</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gekoppeld op</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E8F0]">
              {links.map((link) => {
                const colors = STATUS_COLORS[link.syncStatus] || { bg: 'bg-gray-100', text: 'text-gray-800' };
                return (
                  <tr key={link.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-[#1E293B]">
                      {getEmployeeName(link.employeeId)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 font-mono">
                      {link.nmbrsEmployeeId}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors.bg} ${colors.text}`}>
                        {link.syncStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(link.linkedAt).toLocaleDateString('nl-NL')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {unlinkConfirm === link.employeeId ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleUnlink(link.employeeId)}
                            disabled={unlinkMutation.isPending}
                            className="px-2 py-1 text-xs text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            {unlinkMutation.isPending ? '...' : 'Bevestigen'}
                          </button>
                          <button
                            onClick={() => setUnlinkConfirm(null)}
                            className="px-2 py-1 text-xs text-gray-600 hover:text-gray-800"
                          >
                            Annuleren
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setUnlinkConfirm(link.employeeId)}
                          className="px-2 py-1 text-sm text-red-600 hover:text-red-800"
                        >
                          Ontkoppelen
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-gray-50 border border-[#E2E8F0] rounded-lg p-8 text-center">
          <p className="text-gray-500">Nog geen medewerkers gekoppeld aan Nmbrs.</p>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-3 text-sm text-[#54949E] hover:text-[#2F5A7A]"
            >
              Eerste medewerker koppelen
            </button>
          )}
        </div>
      )}

      {unlinkMutation.isError && (
        <p className="text-sm text-red-600">
          {unlinkMutation.error instanceof Error ? unlinkMutation.error.message : 'Ontkoppeling mislukt'}
        </p>
      )}
    </div>
  );
}
